import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Beer, ClipboardList, LayoutGrid, Users } from 'lucide-react';
import useLandingPages from '../../hooks/useLandingPages';
import { LandingPageSummary, buildFileUrl, landingPath } from '../../services/landingPageService';
import { LandingIcon } from '../landing/landingIcons';
import { currentLang } from '../../i18n';
import { t } from '../../i18n';

// ============================================
// SolutionsTeaser (2026-09-20) – Startseiten-Block „DartCloud für Vereine ·
// Spielstätten · Veranstalter“: 4 Karten (Vereine, Spielstätten, Anmeldung,
// Turniersoftware) mit 16:9-Hero-Bild aus den veröffentlichten
// Landingpages. Nur veröffentlichte Seiten werden verlinkt (keine toten
// Links); ohne veröffentlichte Seiten rendert der Block nichts.
// ============================================

export const TEASER_SLUGS: Array<{ slug: string; icon: LandingIcon }> = [
  { slug: 'dartvereine', icon: Users },
  { slug: 'spielstaetten', icon: Beer },
  { slug: 'turnier-anmeldung', icon: ClipboardList },
  { slug: 'turniersoftware', icon: LayoutGrid },
];

/** Teaser-Karten in fester Reihenfolge, nur veröffentlichte Slugs (pure). */
export const pickTeaserPages = (pages: LandingPageSummary[]): Array<LandingPageSummary & { icon: LandingIcon }> =>
  TEASER_SLUGS.map(({ slug, icon }) => {
    const page = pages.find((p) => p.slug === slug);
    return page ? { ...page, icon } : null;
  }).filter((p): p is LandingPageSummary & { icon: LandingIcon } => p !== null);

const SolutionsTeaser: React.FC<{ className?: string }> = ({ className }) => {
  const lang = currentLang();
  const { pages } = useLandingPages(lang);
  const cards = pickTeaserPages(pages);
  if (cards.length === 0) return null;

  return (
    <section aria-label={t('web.component.solutions-teaser.dartcloud-fuer-vereine')} data-testid="solutions-teaser" className={className}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="section-heading text-base font-bold text-white">{t('web.component.solutions-teaser.dartcloud-fuer-vereine')}</h2>
          <p className="mt-0.5 text-xs text-gray-500">{t('web.component.solutions-teaser.kostenlos-eintragen-turniere')}</p>
        </div>
        <Link
          to={landingPath(null, lang)}
          className="flex shrink-0 items-center gap-1 text-xs font-semibold text-gray-400 transition-colors hover:text-[#3fced1]"
        >
          {t('web.component.solutions-teaser.alle-loesungen')} <ArrowRight size={13} />
        </Link>
      </div>
      <ul className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((card) => {
          const img = buildFileUrl(card.heroImage, 400);
          const Icon = card.icon;
          return (
            <li key={card.slug} className="min-w-0">
              <Link
                to={landingPath(card.slug, lang)}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/60 transition hover:border-[#3fced1]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
              >
                <div className="aspect-[16/9] w-full overflow-hidden bg-zinc-900">
                  {img ? (
                    <img src={img} alt={card.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[#3fced1]">
                      <Icon size={26} aria-hidden="true" />
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="line-clamp-1 text-sm font-bold text-white">{card.title}</h3>
                  {card.subtitle && <p className="mt-0.5 line-clamp-2 text-xs text-gray-400">{card.subtitle}</p>}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default SolutionsTeaser;
